<template>
  <div class="findstore">
    <div v-if="noStoresError" class="errormessage">
      <Alert
        :type="'error'"
        :small="true"
        :text="this.insertString('FindStore', 'NoStoresError')"
        :icon="true"
      />
    </div>
    <section>
      <StoreFinderBlock
        :storeList="sortedByDistanceList"
        :images="storeImages"
        @on-location="onUserLocation"
        @ready="mapReady()"
      />
    </section>
    <section class="grid m-t-36 m-b-60" style="overflow-x: hidden">
      <h1 class="font-responsive-larger">
        {{ insertString("FindStore", "ListTitle") }}
      </h1>
      <div class="grid-12" v-for="(country, index) in groupedList" :key="index">
        <h2 class="font-responsive-large m-t-36 m-b-18">
          {{ mapCountry(country[0]) }}
        </h2>

        <div
          v-for="(store, sindex) in country[1]"
          :key="sindex"
          class="g-6 g-md-3 store-list-store font-responsive-small p-b-24"
        >
          <div class="m-b-12 -bold">{{ store.Model.Name }}</div>
          <div>
            <div v-if="store.Model.Email">
              <span>{{ insertString("Global", "EmailLabel") }}: </span
              ><a
                class="-blue -clickable"
                :href="`mailto:${store.Model.Email}`"
                >{{ store.Model.Email }}</a
              >
            </div>
            <div v-if="store.Model.Phone">
              <span>{{ insertString("GenericForm", "PhoneLabel") }}:</span
              ><a
                class="-blue -clickable"
                :href="`tel::${store.Model.Phone}`"
                >{{ store.Model.Phone }}</a
              >
            </div>
            <div v-if="store.Model.VisitorAddress">
              {{ store.Model.VisitorAddress.Line1 }}
            </div>
          </div>
          <StoreOpeningHours :model="store.Model.OpeningHours" class="m-t-12" />
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import Api from '@/Scripts/api/api';

import pageLoader from '@/Scripts/lib/helper/pageLoader.js';
// import staticStoreList from '@/Scripts/components/pages/FindStore/Assets/staticStoreList.json';
import StoreFinderBlock from '@/Scripts/components/pages/FindStore/StoreFinderBlock.vue';
import StoreOpeningHours from '@/Scripts/components/pages/FindStore/StoreOpeningHours.vue';
import UrlHelper from '@/Scripts/lib/helper/urlHelper.js';
const Alert = () => import(/* webpackChunkName: "Alert" */'@/Scripts/components/shared/Alert/Alert.vue');
function groupBy (list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export default {
  name: 'FindStorePage',
  mixins: [pageLoader],
  components: {
    StoreFinderBlock,
    StoreOpeningHours,
    Alert
  },
  data () {
    return {
      storeList: [],
      countries: null,
      noStoresError: false,
      haveUserLocation: false,
      storeImages: null
    };
  },

  computed: {
    sortedByDistanceList () {
      const list = [...this.storeList].sort(function (a, b) {
        if (a.DistanceKm < b.DistanceKm) return -1;
        if (a.DistanceKm > b.DistanceKm) return 1;
        return 0;
      });
      return list.filter(x => x.Model.IsActive);
    },
    groupedList () {
      const list = [...this.storeList].filter(x => x.Model.IsActive && x.Model.IsRetail).sort(function (a, b) {
        if (a.Country < b.Country) return -1;
        if (a.Country > b.Country) return 1;
        return 0;
      });
      const groupMap = groupBy(list, store => store.Model.VisitorAddress.CountryCode);
      return Array.from(groupMap);
    }
  },
  methods: {
    onUserLocation (coords) {
      if (!this.haveUserLocation) {
        this.getStoreList(coords);
        this.haveUserLocation = true;
      }
    },
    async mapReady () {
      await this.getCountries();
      if (!this.haveUserLocation) {
        await this.getStoreList();
      }
      this.$_removePageLoader();
    },
    mapCountry (code) {
      if (!this.countries) {
        return code;
      }
      const c = this.countries.find(item => item.CountryCode === code);
      if (c) {
        return c.CountryName;
      }
      return code;
    },
    async getCountries () {
      try {
        const response = await Api.get('/api/country/');
        this.countries = response.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getStoreList (coords) {
      this.noStoresError = false;

      const url = UrlHelper.getCurrentUrlWithAction('Search');
      let params = {};
      if (coords) {
        params = {
          latitude: coords.latitude,
          longitude: coords.longitude
        };
      }
      try {
        const result = await Api.getContentByFriendlyUrl(url, params);
        this.storeList.splice(0);
        if (result && result.data) {
          this.storeList = this.mapData(result.data).filter(store => store.Coordinate !== null);
        } else {
          this.noStoresError = true;
        }
      } catch (error) {
        console.log(error);
        this.noStoresError = true;
      }
    },
    mapData (data) {
      data.forEach((item, index) => {
        item.index = index;
        item.Country = this.mapCountry(item.Model.VisitorAddress.CountryCode);
        item.Model.Type = item.Model.IsOutlet ? 'OutletStore' : item.Model.IsRetail ? 'RetailStore' : item.Model.Type || '';
        item.StreetView = {
          Enabled: item.Model.VisitorAddress.IsStreetViewEnabled,
          ForceOutdoor: item.Model.VisitorAddress.IsStreetViewForceOutdoor,
          ForceBest: item.Model.VisitorAddress.IsStreetViewForceBest,
          Latitude: item.Model.VisitorAddress.GoogleMapsLatitude,
          Longitude: item.Model.VisitorAddress.GoogleMapsLongitude
        };
      });
      return data;
    }
  },
  async created () {
    const response = await Api.getContentByFriendlyUrl('/api/store/storeImages');
    if (response && response.data) {
      this.storeImages = response.data.Media;
    }
  }

};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";
.findstore {
  .errormessage {
    position: absolute;
    bottom: 120px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
  }
  .store-list-store {
    border-top: 1px solid $gray-dark;
    padding-top: 12px;
  }
}
</style>
